/**
 * UO-style step-queue animation for **remote** mobiles (players and NPCs).
 *
 * A remote character's authoritative pose arrives as discrete snapshot states:
 * its resting tile and facing simply *are* different from one observation to the
 * next. Classic UO clients never interpolate those raw updates - they queue each
 * observed change as a discrete **step** (synthesizing a turn step for a facing
 * change) and play the queue back at the mobile's own per-tile timings, catching
 * up at double speed when the queue backs up. The result is the familiar UO gait:
 * every remote character walks tile-by-tile exactly like the local one, at the
 * cost of up to ~one step of display latency.
 *
 * This replaces the renderer's previous prev/curr snapshot lerp (and its
 * 1.5-tile teleport heuristic): a tile change of more than one tile, a level
 * change, or an overflowing queue is a genuine relocation and **snaps**.
 *
 * Pure and render-friendly: no Pixi, no DOM, no clock - observations arrive via
 * {@link observe}, time via {@link update}(dtMs) - so it unit-tests in Node.
 */
import type { Direction } from '../sim/direction';

/**
 * One observation of a remote mobile's authoritative state: its resting tile,
 * level, and facing, plus the movement timings (from the entity's serialized
 * mobile config) the synthesized steps animate at. `running` is whether the
 * mobile's last step used the run gait, picking the per-tile duration.
 */
export interface ObservedPose {
  x: number;
  y: number;
  z: number;
  facing: Direction;
  msPerTile: number;
  msPerTileRun: number;
  turnDelayMs: number;
  running: boolean;
}

/**
 * Queue overflow bound: more backlog than the client-side prediction cap (5)
 * means the animation has fallen hopelessly behind - snap instead of replaying
 * a long history at catch-up speed.
 */
const MAX_QUEUE = 5;

/**
 * The hit-stagger hold (Arena hit reactions, PRD #430 Slice A, ADR-0225): on a
 * skill hit the renderer holds this entity's replay for this long, then lets it
 * catch back up - a client-only flinch; position authority is untouched.
 *
 * Deliberately shorter than every per-tile gait time an arena enemy actually
 * moves at, so a single hold can never leave the replay a full tile behind the
 * stream. The applicable floors: arena pursuit is path-driven
 * (`mobile.setPath`) and a routed step is ALWAYS the walk gait
 * (`Mobile.decidePathStep`), so the tightest grunt gait is
 * `ARENA_ENDLESS_MIN_MS_PER_TILE` (130ms - the endless escalation floor under
 * every wave tier's `msPerTile`) and the tightest boss gait is the roster's
 * min `msPerTile` (135ms), against the HALVED boss hold (the caller passes
 * `STAGGER_MS / 2` - shrugging through fire is part of a boss's read). Both
 * bounds are pinned sim-derived in the test, beside a canary that a routed
 * step never runs - if enemies ever run, `msPerTileRun` defaults to
 * `msPerTile / 2` (65ms at the floor, UNDER this hold) and the bound must be
 * re-derived.
 */
export const STAGGER_MS = 80;

/**
 * How much faster the replay clock runs while repaying a stagger hold: the held
 * time is banked as debt and paid back at (rate - 1) extra replay-ms per real
 * ms, so the enemy visibly hastens back onto its authoritative track instead of
 * snapping. 2x matches the classic UO backlog catch-up below, so a staggered
 * enemy and a backlogged one recover at the same familiar pace.
 */
export const STAGGER_CATCHUP_RATE = 2;

/**
 * Longest straight run of tiles a single observation may advance and still be
 * replayed as chained unit steps rather than snapped.
 *
 * The run gait (125ms/tile) is barely slower than the 100ms snapshot broadcast
 * interval (ADR-0163), so a runner covers ~0.8 tiles per snapshot - almost the
 * whole 1-tile budget. The moment a snapshot is dropped or coalesced (TCP
 * head-of-line blocking, a GC/tab hitch, load-shed broadcast), a runner's next
 * observed resting tile is two tiles on. That is an ordinary two-tile run to
 * replay, **not** a teleport: treating it as one (the old `>1` snap) stopped the
 * run cycle and jumped the sprite - the "floating" a runner shows and a walker
 * (0.4 tiles/snapshot, ample margin) never does. A move longer than this, one
 * that is not a straight line of UO steps, or a level change is a genuine
 * relocation and still snaps.
 */
const MAX_REPLAY_TILES = 2;

/** A queued discrete action: a turn-in-place, or a 1-tile step. */
type QueuedStep =
  | { kind: 'turn'; toFacing: Direction; durationMs: number }
  | {
      kind: 'step';
      fromX: number;
      fromY: number;
      toX: number;
      toY: number;
      durationMs: number;
      running: boolean;
    };

export class StepAnimator {
  /** The animated resting pose (the tail of every *completed* step). */
  private x: number;
  private y: number;
  private level: number;
  private restFacing: Direction;
  /** The last pose {@link observe} saw, diffed against to synthesize steps. */
  private observed: ObservedPose;

  private readonly queue: QueuedStep[] = [];
  /** Elapsed ms into the front queue entry. */
  private elapsed = 0;

  /**
   * Remaining hit-stagger hold, ms (PRD #430 Slice A): while positive, `update`
   * consumes real time here *before* the queue plays, freezing the glide in
   * place. Restarted (never stacked) by {@link stagger}.
   */
  private staggerRemainingMs = 0;
  /**
   * Replay time owed from consumed holds, ms: repaid after the hold by running
   * the queue clock {@link STAGGER_CATCHUP_RATE}x until the debt drains, so the
   * replay hastens back onto the stream rather than staying a hold behind.
   */
  private staggerDebtMs = 0;
  /**
   * The landing tile of an in-flight {@link hurl} (the Arena Comet knockback,
   * PRD #419 slice E), `null` outside one. While set, an observation arriving
   * AT this tile is absorbed silently (the authoritative stream confirming the
   * slide already playing) instead of being diffed into a redundant replay or
   * a mid-slide snap; any other observation wins as ever (authority first).
   */
  private hurlDest: { x: number; y: number } | null = null;

  constructor(initial: ObservedPose) {
    this.x = initial.x;
    this.y = initial.y;
    this.level = initial.z;
    this.restFacing = initial.facing;
    this.observed = { ...initial };
  }

  get z(): number {
    return this.level;
  }
  /** The facing to draw: a turn at the queue front flips it the moment it starts. */
  get facing(): Direction {
    const front = this.queue[0];
    return front?.kind === 'turn' ? front.toFacing : this.restFacing;
  }
  /** True while a step (not a turn) is playing - drives the walk cycle. */
  get isMoving(): boolean {
    return this.queue[0]?.kind === 'step';
  }
  get isRunning(): boolean {
    const front = this.queue[0];
    return front?.kind === 'step' && front.running;
  }
  /** Queued actions not yet fully played (for tests/diagnostics). */
  get queueLength(): number {
    return this.queue.length;
  }
  /** True while a hit-stagger hold is freezing the replay (PRD #430 Slice A). */
  get staggered(): boolean {
    return this.staggerRemainingMs > 0;
  }

  /**
   * Hold the replay in place for `holdMs` (the Arena hit stagger, PRD #430
   * Slice A): the glide freezes mid-step, then catches back up at
   * {@link STAGGER_CATCHUP_RATE}x - a proportionally faster glide, never a
   * snap, and never past the last observed pose (the queue *is* the authority
   * ceiling: playing it faster cannot move beyond its final tile).
   *
   * Non-cumulative by design: a retrigger while staggered RESTARTS the hold
   * rather than extending it, so a maxed cascade hitting one elite five times
   * reads as one continuous flinch, not a half-second freeze. Note the restart
   * deliberately does NOT clear `staggerDebtMs`: debt is real held time already
   * owed, so it keeps accumulating under a retrigger chain - self-correcting,
   * since a replay that ever falls hopelessly behind hits the {@link MAX_QUEUE}
   * snap valve, which resets both. Purely a replay timing effect - the
   * authoritative position stream is untouched.
   */
  stagger(holdMs: number = STAGGER_MS): void {
    this.staggerRemainingMs = Math.max(0, holdMs);
  }

  /**
   * Hurl the sprite `from` -> `to` over `durationMs` as ONE multi-tile slide
   * (the Arena Comet knockback's FX-side polish, PRD #419 slice E). The
   * ordinary observation path can't render a shove: a 3-tile (possibly
   * bent-line) jump exceeds the straight-run replay budget and **snaps** - so
   * the renderer drives the slide explicitly off the volley plan's `hurl` op,
   * and the observation seam is taught to agree with it instead of fighting:
   *
   *  - Snapshot **already landed** (broadcast beat the impact instant - the
   *    common order): the sprite snapped to `to`; this replays the slide from
   *    the strike tile, and per-frame observations at `to` diff to zero as ever.
   *  - Snapshot **still pending**: the slide starts now; the eventual
   *    observation AT the hurl's landing tile is absorbed by {@link observe}
   *    (see {@link hurlDest}) rather than snapping mid-slide. Stale pre-shove
   *    observations were diffed to zero long ago, and any *other* pose (the
   *    enemy already chased on) takes the normal path - authority always wins.
   *
   * Replaces any queued replay and running stagger hold outright (the shove
   * IS this instant's truth); a degenerate same-tile hurl is a no-op.
   */
  hurl(from: { x: number; y: number }, to: { x: number; y: number }, durationMs: number): void {
    if (from.x === to.x && from.y === to.y) return;
    this.queue.length = 0;
    this.elapsed = 0;
    this.staggerRemainingMs = 0;
    this.staggerDebtMs = 0;
    this.x = from.x;
    this.y = from.y;
    this.queue.push({
      kind: 'step',
      fromX: from.x,
      fromY: from.y,
      toX: to.x,
      toY: to.y,
      durationMs,
      running: false,
    });
    this.hurlDest = { x: to.x, y: to.y };
  }

  /** Fractional tile for drawing: interpolated along the front step, else the resting tile. */
  renderPosition(): { x: number; y: number } {
    const front = this.queue[0];
    if (front?.kind !== 'step') return { x: this.x, y: this.y };
    const t = front.durationMs > 0 ? this.elapsed / front.durationMs : 1;
    return {
      x: front.fromX + (front.toX - front.fromX) * t,
      y: front.fromY + (front.toY - front.fromY) * t,
    };
  }

  /**
   * Diff a fresh authoritative pose against the last observation and enqueue the
   * discrete actions that explain it: a synthesized **turn** for a facing change,
   * then a **step** per tile of a straight-line move (turn first - a mobile faces
   * its step direction before stepping), the existing 2x catch-up draining any
   * multi-tile backlog. A colinear move of up to {@link MAX_REPLAY_TILES} tiles
   * is a replayable run (a coalesced snapshot of a fast runner); anything longer,
   * a non-straight move, a level change, or a queue overflowing {@link MAX_QUEUE}
   * is a genuine relocation: clear and snap.
   */
  observe(pose: ObservedPose): void {
    // The hurl guard lives only as long as the slide itself (PRD #419 slice E).
    if (this.hurlDest && this.queue.length === 0) this.hurlDest = null;
    // An observation landing exactly on an in-flight hurl's destination is the
    // stream confirming the slide already playing: absorb it (the pose becomes
    // the diff base; a queued turn keeps the facing honest) instead of
    // replaying or snapping the very move being shown. Any other pose - the
    // enemy already moved on, a level change - takes the normal path below.
    if (
      this.hurlDest &&
      pose.x === this.hurlDest.x &&
      pose.y === this.hurlDest.y &&
      pose.z === this.level
    ) {
      const facingChanged = pose.facing !== this.observed.facing;
      this.observed = { ...pose };
      if (facingChanged) {
        this.queue.push({ kind: 'turn', toFacing: pose.facing, durationMs: pose.turnDelayMs });
      }
      return;
    }
    const prev = this.observed;
    const dx = pose.x - prev.x;
    const dy = pose.y - prev.y;
    // A UO step is one of 8 unit directions, so a straight run of steps has
    // dx/dy each zero or of equal magnitude; the tile count is the longer leg.
    const tiles = Math.max(Math.abs(dx), Math.abs(dy));
    const colinear = dx === 0 || dy === 0 || Math.abs(dx) === Math.abs(dy);
    const replayable = colinear && tiles <= MAX_REPLAY_TILES;
    this.observed = { ...pose };

    if (!replayable || pose.z !== this.level) {
      this.snapTo(pose);
      return;
    }
    if (pose.facing !== prev.facing) {
      this.queue.push({ kind: 'turn', toFacing: pose.facing, durationMs: pose.turnDelayMs });
    }
    if (tiles > 0) {
      const sx = Math.sign(dx);
      const sy = Math.sign(dy);
      const durationMs = pose.running ? pose.msPerTileRun : pose.msPerTile;
      // One unit step per tile between the previous resting tile and the new one.
      for (let i = 0; i < tiles; i++) {
        const fromX = prev.x + sx * i;
        const fromY = prev.y + sy * i;
        this.queue.push({
          kind: 'step',
          fromX,
          fromY,
          toX: fromX + sx,
          toY: fromY + sy,
          durationMs,
          running: pose.running,
        });
      }
    }
    if (this.queue.length > MAX_QUEUE) this.snapTo(pose);
  }

  /**
   * Play the queue forward by `dtMs`. While two or more actions are queued the
   * animation has fallen behind the stream, so remaining durations are halved
   * (time runs at 2x) - the classic UO catch-up trick - until the backlog drains.
   * Leftover time chains across completions, so one frame can finish a turn and
   * begin the following step.
   */
  update(dtMs: number): void {
    let budget = dtMs;
    // The hit-stagger hold eats real time first (PRD #430 Slice A): while held
    // the queue does not advance at all - the glide freezes in place - and the
    // held time banks as catch-up debt.
    if (this.staggerRemainingMs > 0 && budget > 0) {
      const held = Math.min(this.staggerRemainingMs, budget);
      this.staggerRemainingMs -= held;
      this.staggerDebtMs += held;
      budget -= held;
    }
    // Repay stagger debt by running the replay clock faster: each real ms
    // plays (rate - 1) extra ms until the debt drains. The queue still ends at
    // the last observed pose, so acceleration can never overshoot authority.
    if (this.staggerDebtMs > 0 && budget > 0) {
      const extra = Math.min(this.staggerDebtMs, budget * (STAGGER_CATCHUP_RATE - 1));
      this.staggerDebtMs -= extra;
      budget += extra;
    }
    while (budget > 0 && this.queue.length > 0) {
      const front = this.queue[0]!;
      const rate = this.queue.length >= 2 ? 2 : 1;
      const remaining = (front.durationMs - this.elapsed) / rate;
      if (budget < remaining) {
        this.elapsed += budget * rate;
        return;
      }
      budget -= remaining;
      // Complete the front action: land the pose it produces.
      if (front.kind === 'step') {
        this.x = front.toX;
        this.y = front.toY;
      } else {
        this.restFacing = front.toFacing;
      }
      this.queue.shift();
      this.elapsed = 0;
    }
  }

  /** Relocate wholesale: drop the queue and rest at the observed pose. */
  private snapTo(pose: ObservedPose): void {
    this.queue.length = 0;
    this.elapsed = 0;
    // A genuine relocation resets the replay state wholesale: there is nothing
    // left to hold or catch up on at the new spot (PRD #430 Slice A) - and an
    // in-flight hurl is abandoned with the rest (authority won).
    this.hurlDest = null;
    this.staggerRemainingMs = 0;
    this.staggerDebtMs = 0;
    this.x = pose.x;
    this.y = pose.y;
    this.level = pose.z;
    this.restFacing = pose.facing;
  }
}
